"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function PremiumProtectionHero() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: dividerRef,
    isVisible: dividerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2793" id="premium-protection-hero" className="relative w-full min-h-screen overflow-hidden dark bg-background text-foreground">
      <div className="relative flex flex-col lg:flex-row min-h-screen">
        <div ref={leftRef} className={`relative w-full lg:w-1/2 min-h-[60vh] lg:min-h-screen bg-background overflow-hidden transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697750/img/pest_infestation_attic.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697750/img/pest_infestation_attic.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697750/img/pest_infestation_attic.png-wvc-sizes" alt="A dark, compromised attic space showing signs of pest intrusion and vulnerability" fetchPriority="high" width={960} height={1080} decoding="async" className="absolute inset-0 w-full h-full object-cover saturate-0 opacity-40 transition-all duration-[2000ms] ease-out hover:saturate-50 hover:opacity-60" />
          <div className="absolute inset-0 bg-background/60" />

          <div className="absolute top-8 left-8 lg:top-12 lg:left-12 z-10">
            <div className="flex items-center gap-3">
              <span className="block h-px w-8 bg-primary/40" />
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">
                01 // Vulnerability
              </span>
            </div>
          </div>
        </div>

        <div ref={rightRef} className={`relative w-full lg:w-1/2 min-h-[60vh] lg:min-h-screen bg-secondary overflow-hidden transition-all duration-1000 ease-out delay-150 ${rightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697750/img/clean_modern_home_exterior.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697750/img/clean_modern_home_exterior.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697750/img/clean_modern_home_exterior.png-wvc-sizes" alt="A pristine, modern home exterior representing a sealed and protected sanctuary" fetchPriority="high" width={960} height={1080} decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[3000ms] ease-out hover:scale-105" />
          <div className="absolute inset-0 bg-secondary/40" />

          <div className="absolute top-8 right-8 lg:top-12 lg:right-12 z-10">
            <div className="flex items-center gap-3">
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-secondary-foreground">
                02 // Fortification
              </span>
              <span className="block h-px w-8 bg-primary" />
            </div>
          </div>
        </div>

        <div ref={dividerRef} className="hidden lg:block absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-px overflow-hidden pointer-events-none z-10">
          <div className={`w-px bg-primary/30 transition-all duration-[1800ms] ease-out ${dividerVisible ? "h-full" : "h-0"}`} />
        </div>

        <div ref={headlineRef} className={`absolute inset-0 flex items-center justify-center px-6 lg:px-12 z-20 pointer-events-none transition-all duration-1000 ease-out delay-300 ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="w-full max-w-5xl text-center pointer-events-auto">
            <div className="mb-6 lg:mb-8 flex justify-center">
              <div className="inline-block bg-background border border-primary/30 px-4 py-1.5 shadow-md">
                <span className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] text-primary font-bold">
                  Attic to Basement Pest Control
                </span>
              </div>
            </div>

            <p className="font-default text-sm sm:text-base uppercase tracking-[0.2em] text-primary font-semibold mb-4">
              Protecting Homes. Protecting Families. It&apos;s Our Promise.
            </p>

            <h1 className="font-serif font-light text-foreground leading-[1.08] tracking-tight text-3xl sm:text-4xl md:text-5xl lg:text-6xl max-w-4xl mx-auto">
              Pest Control in Durham, Raleigh &amp; <span className="text-primary font-normal italic">the Triangle, NC</span>
            </h1>

            <p className="mt-8 mx-auto max-w-xl text-base font-default leading-relaxed text-secondary !uppercase lg:mt-10 md:text-lg"><span className="!normal-case">Elite pest defense from attic to basement. Precision treatments designed to help protect your sanctuary from pests.</span></p>

            <div className="mt-10 lg:mt-12 flex flex-col sm:flex-row items-center justify-center gap-6">
              <Button asChild size="lg" className="group rounded-md px-6 py-4 sm:px-8 sm:py-6 text-base font-default font-bold tracking-widest uppercase bg-primary text-primary-foreground shadow-md hover:bg-background hover:text-primary hover:border hover:border-primary transition-all">
                <Link to="/ContactPage">
                  <span>Secure Your Quote</span>
                  <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Button>

              <Link to="/ServicesPage" className="group relative inline-flex items-center text-sm font-default font-bold tracking-widest uppercase text-foreground">
                <span>Explore Defense Systems</span>
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-primary transition-all duration-500 ease-out group-hover:w-full" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="lg:hidden absolute left-1/2 top-[60vh] -translate-x-1/2 -translate-y-1/2 w-16 h-px bg-primary/50 z-10" />
    </section>;
}